/**
 * useBookingMutations Hook
 *
 * Handles booking create/update/delete operations with:
 * - Automatic cache invalidation with timeout protection
 * - Error handling and rollback
 *
 * All mutations await cache refetch to ensure UI shows updated data
 * before returning control to the caller.
 */

import { useAtom } from "jotai";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createBookingsMutationAtom,
  updateBookingMutationAtom,
  deleteBookingMutationAtom,
} from "@/store/bookingStore";
import { bookingAPI } from "@/services/BookingAPI";
import type { IBooking, IPostBooking } from "@/services/types";
import { BookingType } from "@/services/types";
import {
  invalidateWithTimeout,
  type InvalidationResult,
} from "@/utils/queryUtils";

/**
 * Options for creating bookings
 */
export interface CreateBookingOptions {
  projectId: number | null;
  customerId: number | null;
  employeeId: number;
  hours: number;
  bookingType:
    | typeof BookingType.HARD_BOOKING
    | typeof BookingType.SOFT_BOOKING;
  dates: Date[];
  activityId?: number;
}

/**
 * Options for editing bookings
 */
export interface EditBookingOptions {
  bookingId: number;
  projectId?: number | null;
  customerId?: number | null;
  employeeId?: number | null;
  hours?: number;
  bookingType?: BookingType;
  date?: Date;
}

export function useBookingMutations() {
  const queryClient = useQueryClient();

  // Mutation atoms
  const [createMutation] = useAtom(createBookingsMutationAtom);
  const [updateMutation] = useAtom(updateBookingMutationAtom);
  const [deleteMutation] = useAtom(deleteBookingMutationAtom);

  /**
   * Invalidate bookings cache and wait for refetch to complete.
   * Uses timeout protection to prevent UI from getting stuck.
   */
  const invalidateBookings =
    useCallback(async (): Promise<InvalidationResult> => {
      return invalidateWithTimeout(queryClient, ["bookings"]);
    }, [queryClient]);

  // ============ CREATE BOOKINGS ============

  /**
   * Create multiple bookings
   */
  const createBookings = useCallback(
    async (options: CreateBookingOptions): Promise<IBooking[]> => {
      const {
        projectId,
        customerId,
        employeeId,
        hours,
        bookingType,
        dates,
        activityId = 1,
      } = options;

      // Build post bookings array
      const postBookings: IPostBooking[] = dates.map((date) => ({
        id: null,
        date,
        project: projectId,
        employee: employeeId,
        activity: activityId,
        b_type: bookingType,
        hours,
        customer: customerId,
      }));

      const result = await bookingAPI.createBookings(postBookings);
      await invalidateBookings();
      return result;
    },
    [invalidateBookings],
  );

  /**
   * Create bookings from raw IPostBooking array
   */
  const createBookingsRaw = useCallback(
    async (bookings: IPostBooking[]): Promise<IBooking[]> => {
      const result = await bookingAPI.createBookings(bookings);
      await invalidateBookings();
      return result;
    },
    [invalidateBookings],
  );

  // ============ UPDATE BOOKING ============

  /**
   * Update a single booking
   */
  const updateBooking = useCallback(
    async (options: EditBookingOptions): Promise<IBooking> => {
      const {
        bookingId,
        projectId,
        customerId,
        employeeId,
        hours,
        bookingType,
        date,
      } = options;

      // Build post booking - all fields must be provided to preserve existing values
      const postBooking: IPostBooking = {
        id: bookingId,
        date: date ?? new Date(),
        project: projectId ?? null,
        employee: employeeId ?? null,
        activity: 1, // Hardcoded
        b_type: bookingType ?? BookingType.HARD_BOOKING,
        hours: hours ?? 0,
        customer: customerId ?? null,
      };

      const result = await bookingAPI.updateBooking(postBooking);
      await invalidateBookings();
      return result;
    },
    [invalidateBookings],
  );

  /**
   * Update booking from raw IPostBooking
   */
  const updateBookingRaw = useCallback(
    async (booking: IPostBooking): Promise<IBooking> => {
      const result = await bookingAPI.updateBooking(booking);
      await invalidateBookings();
      return result;
    },
    [invalidateBookings],
  );

  /**
   * Bulk update bookings
   */
  const updateBookings = useCallback(
    async (bookings: IPostBooking[]): Promise<IBooking[]> => {
      const results = await bookingAPI.updateBookings(bookings);
      await invalidateBookings();
      return results;
    },
    [invalidateBookings],
  );

  // ============ DELETE BOOKING ============

  /**
   * Delete a single booking
   */
  const deleteBooking = useCallback(
    async (booking: IBooking): Promise<void> => {
      await bookingAPI.deleteBooking(booking);
      await invalidateBookings();
    },
    [invalidateBookings],
  );

  /**
   * Delete multiple bookings
   */
  const deleteBookings = useCallback(
    async (bookings: IBooking[]): Promise<void> => {
      await bookingAPI.deleteBookings(bookings);
      await invalidateBookings();
    },
    [invalidateBookings],
  );

  // ============ CONFLICT CHECKING ============

  /**
   * Check for conflicts before creating bookings
   */
  const checkConflicts = useCallback(
    async (bookings: IPostBooking[]): Promise<IBooking[]> => {
      return bookingAPI.checkConflicts(bookings);
    },
    [],
  );

  /**
   * Check if any conflicts exist
   */
  const hasConflicts = useCallback(
    async (bookings: IPostBooking[]): Promise<boolean> => {
      const conflicts = await checkConflicts(bookings);
      return conflicts.length > 0;
    },
    [checkConflicts],
  );

  // ============ UTILITY FUNCTIONS ============

  /**
   * Convert IBooking to IPostBooking for editing
   */
  const bookingToPost = useCallback((booking: IBooking): IPostBooking => {
    return {
      id: booking.id,
      date: booking.date,
      project: booking.project?.id ?? null,
      employee: booking.employee?.id ?? null,
      activity: booking.activity?.id ?? 1,
      b_type: booking.b_type,
      hours: booking.hours,
      customer: booking.customer?.id ?? null,
    };
  }, []);

  /**
   * Convert multiple bookings to post format
   */
  const bookingsToPost = useCallback(
    (bookings: IBooking[]): IPostBooking[] => {
      return bookings.map(bookingToPost);
    },
    [bookingToPost],
  );

  return {
    // Create
    createBookings,
    createBookingsRaw,

    // Update
    updateBooking,
    updateBookingRaw,
    updateBookings,

    // Delete
    deleteBooking,
    deleteBookings,

    // Conflicts
    checkConflicts,
    hasConflicts,

    // Utilities
    bookingToPost,
    bookingsToPost,
    invalidateBookings,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
  };
}
